/**
 * Filtre « Collection » des pages collection.
 *
 * Shopify ne sait pas filtrer une collection par une autre, et Liquid ne lit
 * pas les parametres d'URL qui ne sont pas les siens : le serveur renvoie
 * toujours toute la collection. L'intersection se fait donc ici.
 *
 *  - La section `collection-filter-index` donne, dans l'ordre du tri et avec
 *    les filtres natifs appliques, chaque produit et ses collections : de quoi
 *    compter les produits de chaque option et masquer celles qui n'en ont pas.
 *  - Les cartes viennent des pages de la grille rendues par le serveur : elles
 *    gardent les reglages de l'editeur.
 *  - La pagination est refaite sur les seuls produits retenus.
 *
 * L'etat vit dans l'URL (`filtre_collection=<id>`, repetable, et `page`) :
 * les cases sont dans les formulaires de filtres, facets.js les serialise, les
 * pousse dans l'historique et les rejoue au retour arriere. Ce script reprend
 * la main apres chacun de ses rendus (evenement `facets:rendered`).
 *
 * Limite assumee : les compteurs des filtres natifs sont calcules par Shopify
 * sans ce filtre ; le bloc les masque tant qu'une collection est cochee.
 */
(function () {
  'use strict';

  // L'editeur de theme re-execute les scripts de la section (voir
  // sections/collection-advanced.liquid) : une seule instance a la fois.
  if (window.collectionFilterLoaded) return;
  window.collectionFilterLoaded = true;

  var PARAM = 'filtre_collection';
  var INDEX_SECTION = 'collection-filter-index';
  // Parametres sans effet sur la liste de produits renvoyee par le serveur.
  var LOCAL_PARAMS = ['page', 'section_id', 'sections', PARAM];
  // Memes fleches que default_pagination dans sections/collection-advanced.liquid.
  var PREVIOUS = '<i class="fa-solid fa-arrow-left"></i>';
  var NEXT = '<i class="fa-solid fa-arrow-right"></i>';

  // Caches indexes par la requete native : une meme liste de produits.
  var indexes = {};
  var pages = {};
  var cards = {};

  // Seul le dernier rendu demande peut toucher la page.
  var token = 0;
  var currentSearch = '';

  function root() {
    return document.querySelector('[data-collection-filter-root]');
  }

  function gridContainer() {
    return document.getElementById('ProductGridContainer');
  }

  /**
   * Parametres qui decident de la liste de produits, dans leur ordre
   * d'origine : le tri peut y figurer deux fois, et le serveur en retient un.
   */
  function nativeQuery(searchParams) {
    var params = new URLSearchParams(searchParams);
    LOCAL_PARAMS.forEach(function (name) {
      params.delete(name);
    });
    return params.toString();
  }

  function pageQuery(searchParams, page) {
    var params = new URLSearchParams(searchParams);
    params.delete('page');
    if (page > 1) params.append('page', String(page));
    return params.toString();
  }

  function pathWithQuery(query) {
    return window.location.pathname + (query ? '?' + query : '');
  }

  function sectionUrl(sectionId, query, page) {
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId);
    if (query) url += '&' + query;
    if (page > 1) url += '&page=' + page;
    return url;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Modifie le noeud texte en place : le bloc de filtres observe les ajouts et
   * retraits de noeuds du panneau mobile, et rouvrirait ses accordeons.
   */
  function setText(element, text) {
    if (!element) return;

    var node = element.firstChild;
    if (node && node.nodeType === Node.TEXT_NODE && !node.nextSibling) {
      if (node.data !== text) node.data = text;
    } else {
      element.textContent = text;
    }
  }

  function fetchDocument(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Filtre collection : HTTP ' + response.status + ' sur ' + url);
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  /** Oublie une requete echouee, pour qu'un prochain rendu la retente. */
  function remember(cache, key, promise) {
    cache[key] = promise;
    promise.catch(function () {
      if (cache[key] === promise) delete cache[key];
    });
    return promise;
  }

  /* ---------------------------------------------------------------- index */

  function fetchIndexPage(query, page) {
    return fetchDocument(sectionUrl(INDEX_SECTION, query, page)).then(function (doc) {
      var script = doc.querySelector('[data-collection-filter-index]');
      if (!script) throw new Error('Filtre collection : index introuvable');

      var data = JSON.parse(script.textContent);
      return {
        pages: Number(data.pages) || 0,
        items: Array.isArray(data.items) ? data.items : [],
      };
    });
  }

  function buildIndex(items) {
    var order = [];
    var position = {};
    var memberships = {};

    items.forEach(function (item) {
      var id = String(item[0]);
      if (Object.prototype.hasOwnProperty.call(position, id)) return;

      position[id] = order.length;
      order.push(id);
      memberships[id] = (item[1] || []).map(String);
    });

    return { order: order, position: position, memberships: memberships };
  }

  function loadIndex(query) {
    if (indexes[query]) return indexes[query];

    var promise = fetchIndexPage(query, 1).then(function (first) {
      var rest = [];
      for (var page = 2; page <= first.pages; page++) rest.push(fetchIndexPage(query, page));

      return Promise.all(rest).then(function (others) {
        return buildIndex(
          others.reduce(function (items, other) {
            return items.concat(other.items);
          }, first.items)
        );
      });
    });

    return remember(indexes, query, promise);
  }

  function countByCollection(index) {
    var counts = {};
    index.order.forEach(function (id) {
      index.memberships[id].forEach(function (collectionId) {
        counts[collectionId] = (counts[collectionId] || 0) + 1;
      });
    });
    return counts;
  }

  /** Produits d'au moins une des collections cochees, dans l'ordre du tri. */
  function matchingProducts(index, selected) {
    return index.order.filter(function (id) {
      return index.memberships[id].some(function (collectionId) {
        return selected.has(collectionId);
      });
    });
  }

  /* ---------------------------------------------------------------- cartes */

  /** Retient chaque cellule de grille (carte comprise) sous l'id de son produit. */
  function storeCards(grid, query) {
    Array.prototype.forEach.call(grid.children, function (cell) {
      var card = cell.matches('[data-product-id]') ? cell : cell.querySelector('[data-product-id]');
      if (card) cards[query + '#' + card.getAttribute('data-product-id')] = cell.outerHTML;
    });
  }

  function loadPage(sectionId, query, page) {
    var key = query + '#' + page;
    if (pages[key]) return pages[key];

    var promise = fetchDocument(sectionUrl(sectionId, query, page)).then(function (doc) {
      var grid = doc.getElementById('product-grid');
      if (grid) storeCards(grid, query);
    });

    return remember(pages, key, promise);
  }

  function loadCards(ids, index, query, pageSize, sectionId) {
    var lastPage = Math.max(1, Math.ceil(index.order.length / pageSize));

    function missing() {
      return ids.filter(function (id) {
        return !cards[query + '#' + id];
      });
    }

    function serverPage(id) {
      return Math.floor(index.position[id] / pageSize) + 1;
    }

    function fetchPages(list) {
      var unique = {};
      list.forEach(function (page) {
        if (page >= 1 && page <= lastPage) unique[page] = true;
      });
      return Promise.all(
        Object.keys(unique).map(function (page) {
          return loadPage(sectionId, query, Number(page));
        })
      );
    }

    return fetchPages(missing().map(serverPage))
      .then(function () {
        // Deux produits a egalite dans le tri peuvent passer d'une page a
        // l'autre entre deux requetes : on regarde les pages voisines.
        var neighbours = [];
        missing().forEach(function (id) {
          neighbours.push(serverPage(id) - 1, serverPage(id) + 1);
        });
        return fetchPages(neighbours);
      })
      .then(function () {
        var lost = missing();
        if (lost.length) console.warn('Filtre collection : cartes introuvables', lost);

        return ids
          .map(function (id) {
            return cards[query + '#' + id];
          })
          .filter(Boolean);
      });
  }

  /* ---------------------------------------------------------------- rendu */

  function overlayHtml(grid) {
    var overlay = grid.querySelector(':scope > .loading-overlay');
    return overlay ? overlay.outerHTML : '';
  }

  function fillGrid(box, html) {
    var grid = box.querySelector('#product-grid');
    if (!grid) return;

    grid.innerHTML = overlayHtml(grid) + html;
    grid.classList.remove('collection--empty');
    grid.setAttribute('data-collection-filter-rendered', '');

    // Comme facets.js : pas d'animation d'apparition sur une grille re-rendue.
    grid.querySelectorAll('.scroll-trigger').forEach(function (element) {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  function renderCards(box, cells) {
    var poster = box.querySelector('template[data-collection-filter-poster]');
    var posterIndex = Number(box.getAttribute('data-poster-index'));
    var html = '';

    // Meme regle que la boucle Liquid : l'affiche precede le produit de son rang.
    cells.forEach(function (cell, i) {
      if (poster && i === posterIndex) html += poster.innerHTML;
      html += cell;
    });

    fillGrid(box, html);
  }

  function renderMessage(box, name) {
    var template = box.querySelector('template[data-collection-filter-' + name + ']');
    fillGrid(box, template ? template.innerHTML : '');
  }

  /** Reprend le balisage de default_pagination, pour garder son style. */
  function renderPagination(box, page, total) {
    var nav = box.querySelector('.collection__pagination');
    if (!nav) return;

    if (total <= 1) {
      nav.innerHTML = '';
      return;
    }

    function link(target, label) {
      return (
        '<a href="' +
        escapeHtml(pathWithQuery(pageQuery(currentSearch, target))) +
        '" data-collection-filter-page>' +
        label +
        '</a>'
      );
    }

    var parts = [];
    if (page > 1) parts.push('<span class="prev">' + link(page - 1, PREVIOUS) + '</span>');

    var previous = 0;
    for (var p = 1; p <= total; p++) {
      if (p !== 1 && p !== total && Math.abs(p - page) > 2) continue;
      if (previous && p - previous > 1) parts.push('<span class="deco">&hellip;</span>');
      parts.push(p === page ? '<span class="page current">' + p + '</span>' : '<span class="page">' + link(p, p) + '</span>');
      previous = p;
    }

    if (page < total) parts.push('<span class="next">' + link(page + 1, NEXT) + '</span>');
    nav.innerHTML = parts.join(' ');
  }

  function renderProductCount(count) {
    var element = root();
    var template = element && element.getAttribute(count === 1 ? 'data-count-one' : 'data-count-other');
    if (!template) return;

    var text = template.replace('[count]', String(count));
    // .mobile-facets__count : tiroir ; #ProductCount : compteur de la barre (mode toolbar).
    document.querySelectorAll('.mobile-facets__count, #ProductCount').forEach(function (counter) {
      setText(counter, text);
    });
  }

  /* ---------------------------------------------------------------- facette */

  function optionIds() {
    var ids = new Set();
    document.querySelectorAll('[data-collection-filter-option]').forEach(function (option) {
      ids.add(option.getAttribute('data-collection-filter-option'));
    });
    return ids;
  }

  function syncInputs(selected) {
    document.querySelectorAll('input[name="' + PARAM + '"]').forEach(function (input) {
      var checked = selected.has(input.value);
      if (input.checked !== checked) input.checked = checked;

      var label = input.closest('.facets__label');
      if (label) label.classList.toggle('active', checked);
    });
  }

  /**
   * Une option n'apparait que si un produit de la liste courante lui
   * appartient, ou si elle est cochee (pour pouvoir la decocher). Sans
   * compteurs (index en echec), seules les options cochees restent.
   */
  function updateOptions(counts, selected) {
    document.querySelectorAll('[data-collection-filter]').forEach(function (facet) {
      var visible = 0;

      facet.querySelectorAll('[data-collection-filter-option]').forEach(function (option) {
        var id = option.getAttribute('data-collection-filter-option');
        var count = counts ? counts[id] || 0 : 0;
        var show = count > 0 || selected.has(id);

        if (option.hidden === show) option.hidden = !show;
        if (counts) setText(option.querySelector('[data-collection-filter-count]'), '(' + count + ')');
        if (show) visible++;
      });

      if (facet.hidden === visible > 0) facet.hidden = visible === 0;

      var selectedCount = facet.querySelector('[data-collection-filter-selected]');
      if (selectedCount) {
        setText(selectedCount, '(' + selected.size + ')');
        selectedCount.classList.toggle('hidden', selected.size === 0);
      }
    });
  }

  /* ---------------------------------------------------------------- cycle */

  function run(searchParams) {
    var element = root();
    var box = gridContainer();
    if (!element || !box) return;

    var current = ++token;
    currentSearch = searchParams || '';
    box.setAttribute('data-collection-filter-ready', '');

    var params = new URLSearchParams(currentSearch);
    var available = optionIds();
    var selected = new Set(
      params.getAll(PARAM).filter(function (id) {
        return available.has(id);
      })
    );
    var query = nativeQuery(currentSearch);
    var grid = box.querySelector('#product-grid');
    var sectionId = grid && grid.getAttribute('data-id');
    var pageSize = Math.max(1, parseInt(box.getAttribute('data-page-size'), 10) || 12);
    var requestedPage = Math.max(1, parseInt(params.get('page'), 10) || 1);

    syncInputs(selected);
    element.classList.toggle('is-collection-filtered', selected.size > 0);
    box.classList.toggle('collection--filtering', selected.size > 0);

    // La grille affichee sort du serveur : ses cartes resserviront.
    if (grid && !grid.hasAttribute('data-collection-filter-rendered')) storeCards(grid, query);

    loadIndex(query)
      .then(function (index) {
        if (current !== token) return;

        updateOptions(countByCollection(index), selected);

        // Liste vide : la grille du serveur affiche deja son propre message.
        if (!selected.size || !index.order.length || !sectionId) return;

        var ids = matchingProducts(index, selected);
        if (!ids.length) {
          renderMessage(box, 'empty');
          renderPagination(box, 1, 1);
          renderProductCount(0);
          return;
        }

        var total = Math.ceil(ids.length / pageSize);
        var page = Math.min(requestedPage, total);
        var slice = ids.slice((page - 1) * pageSize, page * pageSize);

        return loadCards(slice, index, query, pageSize, sectionId).then(function (cells) {
          if (current !== token) return;

          renderCards(box, cells);
          renderPagination(box, page, total);
          renderProductCount(ids.length);

          // Page au-dela de la derniere (lien ancien) : l'URL suit la page affichee.
          if (page !== requestedPage) {
            currentSearch = pageQuery(currentSearch, page);
            history.replaceState({ searchParams: currentSearch }, '', pathWithQuery(currentSearch));
            if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = currentSearch;
          }

          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
          document.dispatchEvent(new CustomEvent('collection-filter:rendered'));
        });
      })
      .catch(function (error) {
        if (current !== token) return;

        console.error(error);
        updateOptions(null, selected);
        if (selected.size) {
          renderMessage(box, 'error');
          renderPagination(box, 1, 1);
        }
      })
      .then(function () {
        if (current === token) box.classList.remove('collection--filtering');
      });
  }

  function init() {
    run(window.location.search.slice(1));
  }

  function scrollToGrid() {
    var box = gridContainer();
    if (!box) return;

    var top = box.getBoundingClientRect().top + window.pageYOffset - 120;
    if (top < window.pageYOffset) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  document.addEventListener('facets:rendered', function (event) {
    var searchParams = event.detail && event.detail.searchParams;
    run(typeof searchParams === 'string' ? searchParams : window.location.search.slice(1));
  });

  // Une case cochee sur mobile doit l'etre aussi sur desktop, et inversement :
  // seul le formulaire touche est envoye, l'autre doit rester a jour.
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || input.name !== PARAM) return;

    document.querySelectorAll('input[name="' + PARAM + '"]').forEach(function (other) {
      if (other.value !== input.value) return;
      if (other !== input) other.checked = input.checked;

      var label = other.closest('.facets__label');
      if (label) label.classList.toggle('active', input.checked);
    });
  });

  // Pagination et « Effacer » : de vrais liens, repris ici pour passer par
  // facets.js (historique, cache, rendu) plutot que de recharger la page.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var pageLink = target.closest('[data-collection-filter-page]');
    var clearLink = pageLink ? null : target.closest('[data-collection-filter-clear]');
    if (!pageLink && !clearLink) return;
    if (typeof FacetFiltersForm === 'undefined' || !root()) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();

    if (pageLink) {
      FacetFiltersForm.renderPage(new URL(pageLink.href, window.location.href).search.slice(1));
      scrollToGrid();
      return;
    }

    document.querySelectorAll('input[name="' + PARAM + '"]').forEach(function (input) {
      input.checked = false;
    });

    var params = new URLSearchParams(currentSearch);
    params.delete(PARAM);
    params.delete('page');
    FacetFiltersForm.renderPage(params.toString());
  });

  // L'editeur de theme re-rend la section a chaque reglage.
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
